<template>
  <v-card class="email-composer" elevation="8" max-width="800">
    <!-- En-tête -->
    <v-card-title class="d-flex justify-space-between align-center bg-blue-darken-2">
      <span class="text-h6 text-white">Nouveau message</span>
      <v-btn
        icon
        variant="text"
        color="white"
        @click="closeComposer"
        size="small"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-form @submit.prevent="sendEmail" class="email-form">
      <v-card-text>
        <!-- Destinataires -->
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="email.to"
              label="À"
              placeholder="destinataire@example.com"
              required
              multiple
              variant="outlined"
              density="comfortable"
            >
              <template v-slot:append>
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      variant="text"
                      size="small"
                      v-bind="props"
                    >
                      <v-icon>mdi-account-multiple</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="contact in contacts"
                      :key="contact.email"
                      @click="addRecipient(contact.email, 'to')"
                    >
                      <v-list-item-title>{{ contact.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ contact.email }}</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="email.cc"
              label="Cc"
              placeholder="copie@example.com"
              multiple
              variant="outlined"
              density="comfortable"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="email.bcc"
              label="Cci"
              placeholder="copie-cachee@example.com"
              multiple
              variant="outlined"
              density="comfortable"
            />
          </v-col>
        </v-row>

        <!-- Sujet -->
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="email.subject"
              label="Sujet"
              placeholder="Objet du message"
              required
              variant="outlined"
              density="comfortable"
            />
          </v-col>
        </v-row>

        <!-- Éditeur de message -->
        <v-row>
          <v-col cols="12">
            <v-label class="text-caption">Message</v-label>
            <div class="editor-toolbar mb-2">
              <v-btn
                variant="text"
                size="small"
                @click="formatText('bold')"
                title="Gras"
              >
                <v-icon>mdi-format-bold</v-icon>
              </v-btn>
              <v-btn
                variant="text"
                size="small"
                @click="formatText('italic')"
                title="Italique"
              >
                <v-icon>mdi-format-italic</v-icon>
              </v-btn>
              <v-btn
                variant="text"
                size="small"
                @click="formatText('underline')"
                title="Souligné"
              >
                <v-icon>mdi-format-underline</v-icon>
              </v-btn>
              <v-btn
                variant="text"
                size="small"
                @click="insertLink"
                title="Lien"
              >
                <v-icon>mdi-link</v-icon>
              </v-btn>
              <v-btn
                variant="text"
                size="small"
                @click="formatText('insertUnorderedList')"
                title="Liste à puces"
              >
                <v-icon>mdi-format-list-bulleted</v-icon>
              </v-btn>
              <v-btn
                variant="text"
                size="small"
                @click="formatText('insertOrderedList')"
                title="Liste numérotée"
              >
                <v-icon>mdi-format-list-numbered</v-icon>
              </v-btn>
              <v-btn
                variant="text"
                size="small"
                @click="formatText('formatBlock', '<p>')"
                title="Paragraphe"
              >
                <v-icon>mdi-format-paragraph</v-icon>
              </v-btn>
            </div>

            <v-textarea
              ref="messageEditor"
              v-model="email.message"
              variant="outlined"
              placeholder="Tapez votre message ici..."
              auto-grow
              rows="8"
              hide-details
              @keydown.tab="handleTab"
            />
          </v-col>
        </v-row>

        <!-- Pièces jointes -->
        <v-row>
          <v-col cols="12">
            <v-label class="text-caption mb-2">Pièces jointes</v-label>
            <v-file-input
              v-model="newAttachments"
              multiple
              chips
              counter
              variant="outlined"
              density="comfortable"
              label="Ajouter des fichiers"
              prepend-icon="mdi-paperclip"
              @update:model-value="handleFileUpload"
              :hide-details="attachments.length === 0"
            >
              <template v-slot:selection="{ fileNames }">
                <v-chip
                  v-for="fileName in fileNames"
                  :key="fileName"
                  size="small"
                  class="me-2"
                >
                  {{ fileName }}
                </v-chip>
              </template>
            </v-file-input>

            <!-- Liste des pièces jointes -->
            <v-list v-if="attachments.length > 0" density="compact" class="attachment-list">
              <v-list-item
                v-for="(file, index) in attachments"
                :key="index"
                class="px-0"
              >
                <template v-slot:prepend>
                  <v-icon color="blue">
                    {{ getFileIcon(file.type) }}
                  </v-icon>
                </template>
                
                <v-list-item-title class="text-body-2">
                  {{ file.name }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ formatFileSize(file.size) }}
                </v-list-item-subtitle>
                
                <template v-slot:append>
                  <v-btn
                    icon
                    variant="text"
                    size="x-small"
                    @click="removeAttachment(index)"
                    color="error"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn
          variant="text"
          color="grey-darken-1"
          @click="discardEmail"
          :disabled="sending"
        >
          <v-icon start>mdi-delete</v-icon>
          Supprimer
        </v-btn>
        
        <v-btn
          variant="text"
          color="blue-darken-2"
          @click="saveDraft"
          :loading="savingDraft"
          :disabled="sending"
        >
          <v-icon start>mdi-content-save</v-icon>
          Sauvegarder
        </v-btn>
        
        <v-btn
          type="submit"
          color="blue-darken-2"
          :loading="sending"
          :disabled="!canSend"
        >
          <v-icon start>mdi-send</v-icon>
          Envoyer
        </v-btn>
      </v-card-actions>
    </v-form>

    <!-- Snackbar de notification -->
    <v-snackbar
      v-model="notification.show"
      :color="notification.type"
      :timeout="3000"
      location="top right"
    >
      {{ notification.message }}
    </v-snackbar>
  </v-card>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'

// État du composant
const email = reactive({
  to: '',
  cc: '',
  bcc: '',
  subject: '',
  message: ''
})

const attachments = ref([])
const newAttachments = ref([])
const sending = ref(false)
const savingDraft = ref(false)
const messageEditor = ref(null)
const notification = reactive({
  show: false,
  message: '',
  type: 'success'
})

// Contacts pour le menu déroulant
const contacts = ref([
  { name: 'Jean Dupont', email: 'jean.dupont@example.com' },
  { name: 'Marie Martin', email: 'marie.martin@example.com' },
  { name: 'Pierre Durand', email: 'pierre.durand@example.com' }
])

// Computed properties
const canSend = computed(() => {
  return email.to && email.subject && email.message.trim()
})

// Méthodes
const formatText = (command, value = null) => {
  if (messageEditor.value) {
    messageEditor.value.focus()
    document.execCommand(command, false, value)
  }
}

const insertLink = () => {
  const url = prompt('Entrez l\'URL du lien:')
  if (url) {
    formatText('createLink', url)
  }
}

const handleFileUpload = (files) => {
  if (files) {
    files.forEach(file => {
      if (!attachments.value.some(att => att.name === file.name)) {
        attachments.value.push(file)
      }
    })
  }
  newAttachments.value = [] // Reset l'input
}

const removeAttachment = (index) => {
  attachments.value.splice(index, 1)
}

const getFileIcon = (fileType) => {
  if (fileType.includes('image')) return 'mdi-image'
  if (fileType.includes('pdf')) return 'mdi-file-pdf-box'
  if (fileType.includes('word') || fileType.includes('document')) return 'mdi-file-word-box'
  if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'mdi-file-excel-box'
  if (fileType.includes('zip') || fileType.includes('compressed')) return 'mdi-folder-zip'
  return 'mdi-file'
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const handleTab = (event) => {
  event.preventDefault()
  formatText('insertText', '    ') // Insert 4 espaces pour l'indentation
}

const addRecipient = (emailAddress, field) => {
  if (email[field]) {
    email[field] += `, ${emailAddress}`
  } else {
    email[field] = emailAddress
  }
}

const showNotification = (message, type = 'success') => {
  notification.message = message
  notification.type = type
  notification.show = true
}

const sendEmail = async () => {
  if (!canSend.value) return

  sending.value = true

  try {
    // Préparer les données pour l'envoi
    const formData = new FormData()
    formData.append('to', email.to)
    formData.append('cc', email.cc)
    formData.append('bcc', email.bcc)
    formData.append('subject', email.subject)
    formData.append('message', email.message)
    
    // Ajouter les pièces jointes
    attachments.value.forEach(file => {
      formData.append('attachments', file)
    })

    // Ici, vous appelleriez votre API d'envoi d'email
    const response = await fetch('https://my.edisac.com/api/send-email', {
      method: 'POST',
      body: formData
    })

    if (response.ok) {
      showNotification('Email envoyé avec succès!')
      resetForm()
      emit('sent')
    } else {
      throw new Error('Erreur lors de l\'envoi')
    }
  } catch (error) {
    console.error('Erreur d\'envoi:', error)
    showNotification('Erreur lors de l\'envoi de l\'email', 'error')
  } finally {
    sending.value = false
  }
}

const saveDraft = async () => {
  savingDraft.value = true
  
  try {
    // Sauvegarder le brouillon dans le localStorage ou via API
    const draft = {
      to: email.to,
      cc: email.cc,
      bcc: email.bcc,
      subject: email.subject,
      message: email.message,
      savedAt: new Date().toISOString()
    }
    
    localStorage.setItem('emailDraft', JSON.stringify(draft))
    showNotification('Brouillon sauvegardé')
  } catch (error) {
    showNotification('Erreur lors de la sauvegarde', 'error')
  } finally {
    savingDraft.value = false
  }
}

const loadDraft = () => {
  const savedDraft = localStorage.getItem('emailDraft')
  if (savedDraft) {
    const draft = JSON.parse(savedDraft)
    Object.assign(email, draft)
  }
}

const discardEmail = () => {
  if (email.to || email.subject || email.message) {
    if (confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      resetForm()
      localStorage.removeItem('emailDraft')
      showNotification('Message supprimé')
    }
  } else {
    resetForm()
  }
}

const resetForm = () => {
  Object.assign(email, {
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    message: ''
  })
  attachments.value = []
  newAttachments.value = []
}

const closeComposer = () => {
  if (email.to || email.subject || email.message) {
    if (confirm('Voulez-vous sauvegarder ce message comme brouillon ?')) {
      saveDraft().then(() => {
        emit('close')
      })
      return
    }
  }
  emit('close')
}

// Émettre les événements
const emit = defineEmits(['close', 'sent'])

// Cycle de vie
onMounted(() => {
  loadDraft()
})
</script>

<style scoped>
.email-composer {
  font-family: 'Roboto', sans-serif;
}

.editor-toolbar {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
  background: #fafafa;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.attachment-list {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-top: 8px;
  max-height: 200px;
  overflow-y: auto;
}

:deep(.v-field__input) {
  font-size: 14px;
}

:deep(.v-textarea .v-field__input) {
  font-family: 'Roboto', sans-serif;
  line-height: 1.5;
}
</style>